import React, { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import ProctoringEventNotification from "./ProctoringEventNotification";

// ✅ Signaling server URL
const SIGNALING_SERVER =
  process.env.REACT_APP_SIGNALING_SERVER || "wss://talenttalks.vlinkinfo.com/api/ws/signaling"; // change to your actual backend WS URL

// ✅ Fetch dynamic ICE (STUN + TURN) servers from FastAPI backend
async function getIceServers() {
  try {
    // Use REACT_APP_API_BASE if provided, otherwise default to the deployed backend domain
    const apiBase = process.env.REACT_APP_API_BASE || "https://talenttalks.vlinkinfo.com";
    const res = await fetch(`${apiBase}/api/webrtc/turn-credentials`);
    const data = await res.json();
    console.log("Fetched ICE servers:", data);
    return data;
  } catch (err) {
    console.error("Failed to fetch ICE servers, using fallback:", err);
    return {
      iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
      ],
    };
  }
}

// ---------------------- Utility ----------------------
function sendMessage(socket, message) {
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(message));
  }
}

const isMobileDevice = () =>
  /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const getUserFacingCameraStream = async ({
  width = 1280,
  height = 720,
} = {}) => {
  const baseVideo = {
    width: { ideal: width },
    height: { ideal: height },
  };

  try {
    return await navigator.mediaDevices.getUserMedia({
      video: { ...baseVideo, facingMode: { exact: "user" } },
      audio: false,
    });
  } catch (exactErr) {
    console.warn("Front camera exact constraint failed:", exactErr);
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const frontCamera = devices.find(
      (device) =>
        device.kind === "videoinput" &&
        /front|user|face/i.test(device.label || "")
    );

    if (frontCamera) {
      return await navigator.mediaDevices.getUserMedia({
        video: {
          ...baseVideo,
          deviceId: { exact: frontCamera.deviceId },
        },
        audio: false,
      });
    }
  } catch (deviceErr) {
    console.warn("Front camera device lookup failed:", deviceErr);
  }

  return navigator.mediaDevices.getUserMedia({
    video: { ...baseVideo, facingMode: "user" },
    audio: false,
  });
};

const isUserFacingVideoTrack = (track) => {
  const settings = track.getSettings?.() || {};
  const label = track.label || "";

  if (settings.facingMode === "user") return true;
  if (settings.facingMode === "environment") return false;
  if (settings.displaySurface) return false;

  return /front|user|face/i.test(label) && !/back|rear|environment|screen|display/i.test(label);
};

// ---------------------- Candidate Component ----------------------
export const CandidateWebRTC = forwardRef(
  ({ interviewId, stream, previewStream, onTranscriptSend, proctoringEvent, onDismissEvent, interviewEnded = false }, ref) => {
    const remoteVideoRef = useRef(null);
    const localCameraRef = useRef(null);
    const peerRef = useRef(null);
    const socketRef = useRef(null);
    const localCameraStreamRef = useRef(null);
    const screenStreamRef = useRef(null);
    const iceServersRef = useRef(null);

    // Allow parent to send transcript
    useImperativeHandle(ref, () => ({
      sendTranscript: (question, answer) => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          const msg = {
            type: "transcript",
            question,
            answer,
            timestamp: Date.now(),
          };
          socketRef.current.send(JSON.stringify(msg));
          if (onTranscriptSend) onTranscriptSend(msg);
        }
      },
    }));

    // Fetch ICE servers independently on mount
    useEffect(() => {
      getIceServers()
        .then((data) => {
          iceServersRef.current = data;
        })
        .catch((err) => {
          console.error("Failed to fetch ICE servers:", err);
        });
    }, []);

    useEffect(() => {
      let disposed = false;
      const localCameraVideo = localCameraRef.current;
      let retryTimer = null;
      let connectionGeneration = 0;
      let peerGeneration = 0;
      let reconnectAttempt = 0;
      let stableTimer = null;
      let peerDisconnectTimer = null;
      let peer = null;
      let socket = null;
      let screenStream = stream;
      let cameraStream = null;

      const clearPeer = () => {
        peerGeneration += 1;
        clearTimeout(peerDisconnectTimer);
        peerDisconnectTimer = null;
        if (peer) {
          peer.ontrack = null;
          peer.onicecandidate = null;
          peer.onconnectionstatechange = null;
          try {
            peer.close();
          } catch (error) {
            console.warn("Candidate peer cleanup failed:", error);
          }
          peer = null;
        }
        peerRef.current = null;
      };

      const scheduleReconnect = () => {
        if (disposed || interviewEnded || retryTimer) return;
        const delay = Math.min(1000 * 2 ** reconnectAttempt, 10000);
        reconnectAttempt += 1;
        retryTimer = setTimeout(() => {
          retryTimer = null;
          connectSignaling();
        }, delay);
      };

      const createPeer = async (socketForPeer, generation, sendOffer) => {
        clearPeer();
        const currentPeerGeneration = peerGeneration;
        const peerIceServers = iceServersRef.current || (await getIceServers());
        if (
          disposed ||
          generation !== connectionGeneration ||
          currentPeerGeneration !== peerGeneration
        ) return;
        peer = new RTCPeerConnection({ iceServers: peerIceServers.iceServers });
        const currentPeer = peer;
        peerRef.current = currentPeer;
        let hasRemoteDescription = false;
        let creatingOffer = false;
        const queuedLocalCandidates = [];
        const queuedRemoteCandidates = [];

        screenStream?.getTracks().forEach((track) => {
          if (track.readyState === "live") currentPeer.addTrack(track, screenStream);
          track.onended = () => console.warn("WebRTC source track ended:", track.kind);
        });
        currentPeer.ontrack = (event) => {
          if (peer !== currentPeer || disposed || !remoteVideoRef.current) return;
          remoteVideoRef.current.srcObject = event.streams[0];
          remoteVideoRef.current.play().catch((error) => {
            console.warn("Remote video autoplay was blocked:", error);
          });
        };
        currentPeer.onicecandidate = (event) => {
          if (peer !== currentPeer || !event.candidate) return;
          if (socketForPeer.readyState === WebSocket.OPEN) {
            sendMessage(socketForPeer, { type: "candidate", candidate: event.candidate });
          } else {
            queuedLocalCandidates.push(event.candidate);
          }
        };
        currentPeer.onconnectionstatechange = () => {
          if (peer !== currentPeer || disposed) return;
          const state = currentPeer.connectionState || currentPeer.iceConnectionState;
          if (state === "connected") {
            clearTimeout(peerDisconnectTimer);
            peerDisconnectTimer = null;
          } else if (state === "disconnected") {
            clearTimeout(peerDisconnectTimer);
            peerDisconnectTimer = setTimeout(() => {
              if (peer === currentPeer && currentPeer.connectionState === "disconnected") {
                try {
                  socketForPeer.close(1012, "WebRTC connection disconnected");
                } catch (error) {
                  console.warn("Failed to restart candidate signaling:", error);
                }
              }
            }, 4000);
          } else if (state === "failed" || state === "closed") {
            try {
              socketForPeer.close(1012, "WebRTC connection failed");
            } catch (error) {
              console.warn("Failed to restart candidate signaling:", error);
            }
          }
        };

        const offer = async () => {
          if (
            creatingOffer ||
            peer !== currentPeer ||
            socketForPeer.readyState !== WebSocket.OPEN
          ) return;
          creatingOffer = true;
          try {
            const localOffer = await currentPeer.createOffer();
            if (peer !== currentPeer || generation !== connectionGeneration) return;
            await currentPeer.setLocalDescription(localOffer);
            if (peer !== currentPeer || generation !== connectionGeneration) return;
            sendMessage(socketForPeer, { type: "offer", offer: currentPeer.localDescription });
            while (queuedLocalCandidates.length > 0) {
              sendMessage(socketForPeer, {
                type: "candidate",
                candidate: queuedLocalCandidates.shift(),
              });
            }
          } catch (error) {
            console.error("Candidate WebRTC offer failed:", error);
          } finally {
            creatingOffer = false;
          }
        };

        currentPeer.__receiveOffer = offer;
        currentPeer.__receiveRemoteCandidate = async (candidate) => {
          if (peer !== currentPeer) return;
          if (!hasRemoteDescription) {
            queuedRemoteCandidates.push(candidate);
            return;
          }
          await currentPeer.addIceCandidate(new RTCIceCandidate(candidate));
        };
        currentPeer.__setRemoteAnswer = async (answer) => {
          if (peer !== currentPeer) return;
          await currentPeer.setRemoteDescription(new RTCSessionDescription(answer));
          hasRemoteDescription = true;
          while (queuedRemoteCandidates.length > 0) {
            await currentPeer.addIceCandidate(
              new RTCIceCandidate(queuedRemoteCandidates.shift()),
            );
          }
        };

        if (sendOffer) await offer();
      };

      const connectSignaling = async () => {
        if (disposed || interviewEnded || !screenStream) return;
        const generation = ++connectionGeneration;
        try {
          const nextSocket = new WebSocket(
            `${SIGNALING_SERVER}?interviewId=${interviewId}&role=candidate`,
          );
          socket = nextSocket;
          socketRef.current = nextSocket;
          let peerCreation = null;

          const makeOffer = () => {
            if (!peerCreation) {
              peerCreation = createPeer(nextSocket, generation, false);
            }
            void peerCreation.then(() => {
              if (peer?.__receiveOffer) return peer.__receiveOffer();
              return undefined;
            }).catch((error) => {
              console.error("Could not prepare a WebRTC offer:", error);
            });
          };

          nextSocket.onopen = () => {
            if (disposed || generation !== connectionGeneration) return;
            clearTimeout(stableTimer);
            stableTimer = setTimeout(() => {
              reconnectAttempt = 0;
            }, 10000);
            makeOffer();
          };
          nextSocket.onmessage = async (event) => {
            if (disposed || generation !== connectionGeneration) return;
            try {
              const message = JSON.parse(event.data);
              if (message.type === "restart" || message.type === "admin-joined") {
                peerCreation = createPeer(nextSocket, generation, true);
                await peerCreation;
              } else if (message.type === "answer" && peer?.__setRemoteAnswer) {
                await peer.__setRemoteAnswer(message.answer);
              } else if (message.type === "candidate" && peer?.__receiveRemoteCandidate) {
                await peer.__receiveRemoteCandidate(message.candidate);
              }
            } catch (error) {
              console.error("Candidate signaling message could not be applied:", error);
            }
          };
          nextSocket.onerror = () => {
            if (generation === connectionGeneration && !disposed) nextSocket.close();
          };
          nextSocket.onclose = () => {
            if (disposed || generation !== connectionGeneration) return;
            socketRef.current = null;
            clearTimeout(stableTimer);
            clearPeer();
            scheduleReconnect();
          };
        } catch (error) {
          console.error("Candidate signaling connection failed:", error);
          scheduleReconnect();
        }
      };

      const init = async () => {
        if (interviewEnded) return;
        if (!screenStream) return;
        screenStreamRef.current = screenStream;
        try {
          const isMobile = isMobileDevice();
          const previewTracks = previewStream?.getVideoTracks?.() || [];
          const frontTracks = (screenStream.getVideoTracks?.() || []).filter(
            isUserFacingVideoTrack,
          );
          if (previewTracks.length > 0) {
            cameraStream = previewStream;
          } else if (isMobile && frontTracks.length > 0) {
            cameraStream = new MediaStream(frontTracks);
          } else {
            try {
              cameraStream = await getUserFacingCameraStream();
              localCameraStreamRef.current = cameraStream;
            } catch (error) {
              console.warn("Could not get front camera stream:", error);
              cameraStream = screenStream;
            }
          }
          if (localCameraRef.current && cameraStream) {
            localCameraRef.current.srcObject = cameraStream;
          }
          if (!iceServersRef.current) iceServersRef.current = await getIceServers();
          await connectSignaling();
        } catch (error) {
          console.error("Candidate WebRTC initialization failed:", error);
          scheduleReconnect();
        }
      };

      void init();

      return () => {
        disposed = true;
        clearTimeout(retryTimer);
        clearTimeout(stableTimer);
        clearTimeout(peerDisconnectTimer);
        const oldSocket = socket;
        socket = null;
        socketRef.current = null;
        try {
          oldSocket?.close();
        } catch (error) {
          console.warn("Candidate signaling cleanup failed:", error);
        }
        clearPeer();
        // Stop local video
        try {
          if (localCameraVideo) localCameraVideo.srcObject = null;
        } catch (e) {}

        // Stop local camera stream
        try {
          if (localCameraStreamRef.current) {
            localCameraStreamRef.current.getTracks().forEach((t) => {
              t.stop();
            });
            localCameraStreamRef.current = null;
          }
        } catch (e) {}

        // Stop screen stream (only if we created it, not if passed in)
        try {
          if (screenStreamRef.current && !stream) {
            screenStreamRef.current.getTracks().forEach((t) => {
              t.stop();
            });
            screenStreamRef.current = null;
          }
        } catch (e) {}

      };
    }, [interviewId, stream, previewStream, interviewEnded]);

    return (
      <div className="w-full h-full flex items-center justify-center bg-black overflow-hidden relative">
        <video
          ref={localCameraRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />

        {/* Proctoring Event Notification - Bottom Center */}
        {proctoringEvent && (
<div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 z-10 w-full max-w-2xl px-2">
            <ProctoringEventNotification
              event={proctoringEvent}
              onDismiss={onDismissEvent}
            />
          </div>
        )}
      </div>
    );
  }
);

// ---------------------- Admin Component ----------------------
export const AdminWebRTC = ({
  interviewId,
  onCandidateStarted = () => {},
  onCandidateEnded = () => {},
  onTranscript = () => {},
}) => {
  const remoteVideoRef = useRef(null);
  const peerRef = useRef(null);
  const socketRef = useRef(null);
  const callbacksRef = useRef({
    onCandidateStarted,
    onCandidateEnded,
    onTranscript,
  });
  callbacksRef.current = { onCandidateStarted, onCandidateEnded, onTranscript };

  useEffect(() => {
    let disposed = false;
    let retryTimer = null;
    let stableTimer = null;
    let peerDisconnectTimer = null;
    let reconnectAttempt = 0;
    let socketGeneration = 0;
    let peer = null;
    let iceServers = null;
    let started = false;

    const closePeer = () => {
      clearTimeout(peerDisconnectTimer);
      peerDisconnectTimer = null;
      if (!peer) return;
      peer.ontrack = null;
      peer.onicecandidate = null;
      peer.onconnectionstatechange = null;
      try {
        peer.close();
      } catch (error) {
        console.warn("Admin peer cleanup failed:", error);
      }
      peer = null;
      peerRef.current = null;
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    };

    const scheduleReconnect = () => {
      if (disposed || retryTimer) return;
      const delay = Math.min(1000 * 2 ** reconnectAttempt, 10000);
      reconnectAttempt += 1;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        connectSignaling();
      }, delay);
    };

    const createAnswerPeer = async (socket, generation, offer) => {
      closePeer();
      const currentPeer = new RTCPeerConnection({ iceServers });
      peer = currentPeer;
      peerRef.current = currentPeer;
      let remoteDescriptionReady = false;
      const pendingCandidates = [];
      currentPeer.__receiveCandidate = async (candidate) => {
        if (peer !== currentPeer) return;
        if (!remoteDescriptionReady) {
          pendingCandidates.push(candidate);
          return;
        }
        await currentPeer.addIceCandidate(new RTCIceCandidate(candidate));
      };

      currentPeer.ontrack = (event) => {
        if (peer !== currentPeer || disposed || !remoteVideoRef.current) return;
        remoteVideoRef.current.srcObject = event.streams[0];
        remoteVideoRef.current.play().catch((error) => {
          console.warn("Remote video autoplay was blocked:", error);
        });
      };
      currentPeer.onicecandidate = (event) => {
        if (
          peer === currentPeer &&
          event.candidate &&
          socketGeneration === generation
        ) {
          sendMessage(socket, { type: "candidate", candidate: event.candidate });
        }
      };
      currentPeer.onconnectionstatechange = () => {
        if (peer !== currentPeer || disposed) return;
        const state = currentPeer.connectionState || currentPeer.iceConnectionState;
        if (state === "connected") {
          clearTimeout(peerDisconnectTimer);
          peerDisconnectTimer = null;
        } else if (state === "disconnected" || state === "failed") {
          clearTimeout(peerDisconnectTimer);
          peerDisconnectTimer = setTimeout(() => {
            if (peer !== currentPeer || socketGeneration !== generation) return;
            sendMessage(socket, { type: "restart" });
            closePeer();
          }, state === "failed" ? 0 : 4000);
        }
      };

      try {
        await currentPeer.setRemoteDescription(new RTCSessionDescription(offer));
        if (peer !== currentPeer || socketGeneration !== generation) return;
        remoteDescriptionReady = true;
        while (pendingCandidates.length > 0) {
          await currentPeer.addIceCandidate(
            new RTCIceCandidate(pendingCandidates.shift()),
          );
        }
        const answer = await currentPeer.createAnswer();
        if (peer !== currentPeer || socketGeneration !== generation) return;
        await currentPeer.setLocalDescription(answer);
        sendMessage(socket, { type: "answer", answer: currentPeer.localDescription });
      } catch (error) {
        console.error("Admin WebRTC offer/answer negotiation failed:", error);
        sendMessage(socket, { type: "restart" });
        if (peer === currentPeer) closePeer();
      }

    };

    const connectSignaling = () => {
      if (disposed) return;
      const generation = ++socketGeneration;
      try {
        const socket = new WebSocket(
          `${SIGNALING_SERVER}?interviewId=${interviewId}&role=admin`,
        );
        socketRef.current = socket;
        socket.onopen = () => {
          if (disposed || generation !== socketGeneration) return;
          clearTimeout(stableTimer);
          stableTimer = setTimeout(() => {
            reconnectAttempt = 0;
          }, 10000);
        };
        socket.onmessage = async (event) => {
          if (disposed || generation !== socketGeneration) return;
          try {
            const message = JSON.parse(event.data);
            if (message.type === "candidate_status") {
              return;
            }
            if (
              !started &&
              (message.type === "offer" ||
                message.type === "candidate" ||
                message.type === "transcript")
            ) {
              started = true;
              callbacksRef.current.onCandidateStarted();
            }
            if (message.type === "offer") {
              await createAnswerPeer(socket, generation, message.offer);
            } else if (message.type === "candidate") {
              if (peer?.__receiveCandidate) {
                await peer.__receiveCandidate(message.candidate);
              }
            } else if (message.type === "transcript") {
              callbacksRef.current.onTranscript(message);
              const text = `${(message.question || "").toLowerCase()} ${(message.answer || "").toLowerCase()}`;
              if (text.includes("interview finished") || text.includes("thank you")) {
                callbacksRef.current.onCandidateEnded();
              }
            }
          } catch (error) {
            console.error("Admin signaling message could not be applied:", error);
          }
        };
        socket.onerror = () => {
          if (generation === socketGeneration && !disposed) socket.close();
        };
        socket.onclose = () => {
          if (disposed || generation !== socketGeneration) return;
          socketRef.current = null;
          clearTimeout(stableTimer);
          closePeer();
          scheduleReconnect();
        };
      } catch (error) {
        console.error("Admin signaling connection failed:", error);
        scheduleReconnect();
      }
    };

    const init = async () => {
      try {
        const data = await getIceServers();
        if (disposed) return;
        iceServers = data.iceServers;
        connectSignaling();
      } catch (error) {
        console.error("Could not initialize admin WebRTC:", error);
        scheduleReconnect();
      }
    };

    void init();

    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      clearTimeout(stableTimer);
      clearTimeout(peerDisconnectTimer);
      socketGeneration += 1;
      const socket = socketRef.current;
      socketRef.current = null;
      try {
        socket?.close();
      } catch (error) {
        console.warn("Admin signaling cleanup failed:", error);
      }
      closePeer();
    };
  }, [interviewId]);

  return (
    <div className="w-full h-full flex items-center justify-center bg-black overflow-hidden">
      <video
        ref={remoteVideoRef}
        autoPlay
        playsInline
        className="w-full h-full object-cover"
      />
    </div>
  );
};
